import {
  Backdrop,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useAddRiwayatPekerjaan } from "../../../../api/effects/mutations/RiwayatPekerjaan/addRiwayatPekerjaan";
import { useEditRiwayatPekerjaan } from "../../../../api/effects/mutations/RiwayatPekerjaan/editRiwayatPekerjaan";
import { useFetchRiwayatPekerjaan } from "../../../../api/effects/mutations/RiwayatPekerjaan/useFetchRiwayatPekerjaan";
import InputDate from "../../../../components/InputDate";
import { formatDate, toIsoStringDate } from "../../../../utils/dateConvert";
import separateNumber, { formatNumber } from "../../../../utils/formatPrice";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import InputDatev2 from "../../../../components/InputDateForm";
import { AddRiwayatPekerjaanPayload } from "../../../../models/RiwayatPekerjaan";
import { InputTextFieldForm } from "../../../../components/InputTextFieldForm";
import InputDateTimeForm from "../../../../components/InputDateForm";
import InputDateForm from "../../../../components/InputDateForm";
import InputCheckboxForm from "../../../../components/InputCheckboxForm";
import { parse } from "path";

const SquareGrid = styled(Grid)(({ theme }) => ({
  backgroundColor: "#FFF",
  height: "auto",
  width: "90%",
  boxShadow: "1px 1px 10px #888888",
  borderRadius: "10px",
  padding: "1%",
  [theme.breakpoints.down("sm")]: {
    padding: "10px",
  },
  margin: "1%",
}));

const FullButton = styled(Button)(({ theme }) => ({
  width: "70%",
}));

const ScrollGrid = styled(Grid)(({ theme }) => ({
  overflow: "auto",
  maxHeight: "90vh",
}));

type FormValues = {
  perusahaan: string;
  jenisUsaha: string;
  jabatanAwal: string;
  jabatanAkhir: string;
  tahunMasuk: string;
  tahunKeluar?: string;
  jumlahKaryawan: string;
  atasanLangsung: string;
  direktur: string;
  gajiTerakhir?: string;
  alasanBerhenti: string;
  masihBekerja: boolean;
  noGaji: boolean;
  noJumlahKaryawan: boolean;
};

type Props = {
  open: boolean;
  handleClose: () => void;
  ID?: number;
};

export default function RiwayatPekerjaanModal({
  ID = -1,
  open,
  handleClose,
}: Props) {

  const { mutateAsync: GetRiwayatPekerjaanData } = useFetchRiwayatPekerjaan({
    onSuccess(data, variables) {
      if (variables !== ID) {
        return;
      }

      setValue('perusahaan', data.NAMA_PERUSAHAAN);
      setValue('jenisUsaha', data.JENIS_USAHA);
      setValue('jabatanAwal', data.JABATAN_AWAL);
      setValue('jabatanAkhir', data.JABATAN_AKHIR);
      setValue('tahunMasuk', formatDate(data.TAHUN_MASUK));
      setValue('tahunKeluar', data.TAHUN_KELUAR == null ? "" : formatDate(data.TAHUN_KELUAR));
      setValue('jumlahKaryawan', String(data.JUMLAH_KARYAWAN ?? ""));
      setValue('atasanLangsung', data.NAMA_ATASAN_LANGSUNG);
      setValue('direktur', data.NAMA_DIREKTUR);
      setValue('gajiTerakhir', String(data.GAJI_TERAKHIR ?? ""));
      setValue('alasanBerhenti', data.ALASAN_BERHENTI);

      setValue('noGaji', data.GAJI_TERAKHIR === null || data.GAJI_TERAKHIR === undefined);
      setValue('masihBekerja', data.TAHUN_KELUAR === null || data.TAHUN_KELUAR === undefined);
      setValue('noJumlahKaryawan', data.JUMLAH_KARYAWAN === 0)
    },
    onError() {
      console.log("Error");
    },
  });

  useEffect(() => {
    if (ID > 0) {
      GetRiwayatPekerjaanData(ID);
    }
  }, [ID, open]);

  const parseDate = (dateStr: string) => {
    const [day, month, year] = dateStr.split('-');
    return new Date(Number(year), Number(month) - 1, Number(day));
  };

  const schema = z.object({
    perusahaan: z.string({ required_error: "Perusahaan field is required" }).min(1, "Perusahaan field is required"),
    jenisUsaha: z.string({ required_error: "Jenis Usaha field is required" }).min(1, "Jenis Usaha field is required"),
    jabatanAwal: z.string({ required_error: "Jabatan Awal field is required" }).min(1, "Jabatan Awal field is required"),
    jabatanAkhir: z.string({ required_error: "Jabatan Akhir field is required" }).min(1, "Jabatan Akhir field is required"),

    tahunMasuk: z
      .string({ required_error: "Tahun Masuk field is required" })
      .min(1, { message: "Tahun Masuk field is required" })
      .refine(val => {
        const date = parseDate(val);
        return !isNaN(date.getTime());
      }, { message: "Invalid date format" })
      .refine(val => {
        const date = new Date(val);
        const minDate = new Date(1900, 0, 1);
        const maxDate = new Date();
        return date >= minDate && date <= maxDate;
      }, { message: "Date must be between January 1, 1900 and today" }),

    tahunKeluar: z
      .string().optional()
      .refine(val => {
        if (!val) return true;
        const date = parseDate(val);
        return !isNaN(date.getTime());
      }, { message: "Invalid date format" })
      .refine(val => {
        if (!val) return true;
        const date = new Date(val);
        const minDate = new Date(1900, 0, 1);
        const maxDate = new Date();
        return date >= minDate && date <= maxDate;
      }, { message: "Date must be between January 1, 1900 and today" }),

    atasanLangsung: z.string({ required_error: "Atasan Langsung field is required" }).min(1, "Atasan Langsung field is required"),
    direktur: z.string({ required_error: "Direktur field is required" }).min(1, "Direktur field is required"),
    alasanBerhenti: z
      .string({ required_error: "Alasan Berhenti field is required" })
      .min(1, "Alasan Berhenti field is required")
      .max(300, { message: "Alasan Berhenti can't be longer than 300 characters" }),
    gajiTerakhir: z.string().optional().refine((val) => !val || /^-?\d+$/.test(val), "Please enter a valid number"),
    masihBekerja: z.boolean(),
    noGaji: z.boolean(),
    jumlahKaryawan: z.string().optional().refine((val) => !val || /^-?\d+$/.test(val), "Please enter a valid number"),
  }).superRefine((data, ctx) => {
    if (!data.masihBekerja && (!data.tahunKeluar || data.tahunKeluar.trim() === '')) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['tahunKeluar'],
        message: 'Tahun Keluar field is required',
      });
    }

    if (data.tahunMasuk && data.tahunKeluar) {
      const masuk = parseDate(data.tahunMasuk);
      const keluar = parseDate(data.tahunKeluar);
      if (masuk > keluar) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['tahunKeluar'],
          message: "End Date can't be before Start Date",
        });
      }
    }
  });;

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    getValues,
    watch,
    trigger,
  } = useForm<FormValues>({
    mode: "onSubmit",
    defaultValues: {
      perusahaan: '',
      jenisUsaha: '',
      jabatanAwal: '',
      jabatanAkhir: '',
      tahunMasuk: '',
      tahunKeluar: '',
      jumlahKaryawan: '',
      atasanLangsung: '',
      direktur: '',
      gajiTerakhir: '',
      alasanBerhenti: '',
      masihBekerja: false,
      noGaji: false,
      noJumlahKaryawan: false,
    },
    resolver: zodResolver(schema),
  });

  const masihBekerja = watch("masihBekerja");
  const noGaji = watch("noGaji");
  const noJumlahKaryawan = watch("noJumlahKaryawan");

  const { mutateAsync: AddRiwayatPekerjaan, isLoading: isAdding } = useAddRiwayatPekerjaan({
    onSuccess() {
      handleModalClose();
      console.log("Success");
    },
    onError() {
      console.log("Error");
    },
  });

  const { mutateAsync: EditRiwayatPekerjaan, isLoading: isEditing } = useEditRiwayatPekerjaan({
    onSuccess() {
      handleModalClose();
    },
    onError() {
      console.log("Error");
    },
  });
  const handleModalClose = () => {
    reset();
    handleClose();
  };
  const onSubmit: SubmitHandler<FormValues> = async (data: FormValues) => {
    const payload = {
      NAMA_PERUSAHAAN: data.perusahaan,
      JENIS_USAHA: data.jenisUsaha,
      JABATAN_AWAL: data.jabatanAwal,
      JABATAN_AKHIR: data.jabatanAkhir,
      TAHUN_MASUK: toIsoStringDate(data.tahunMasuk),
      TAHUN_KELUAR: getValues('masihBekerja') || data.tahunKeluar === '' || data.tahunKeluar === undefined ? null : toIsoStringDate(data.tahunKeluar!),
      JUMLAH_KARYAWAN: formatNumber(data.jumlahKaryawan),
      NAMA_ATASAN_LANGSUNG: data.atasanLangsung,
      NAMA_DIREKTUR: data.direktur,
      GAJI_TERAKHIR: getValues('noGaji') || data.gajiTerakhir === '' || data.gajiTerakhir === undefined ? null : formatNumber(data.gajiTerakhir),
      ALASAN_BERHENTI: data.alasanBerhenti,
    };

    if (ID > 0) {
      await EditRiwayatPekerjaan({ RIWAYAT_PEKERJAAN_ID: ID, ...payload });
    } else {
      await AddRiwayatPekerjaan(payload);
    }


  };


  useEffect(() => {
    // Only run this logic when the modal is visible.
    if (open) {
      if (ID > 0) {
        // If we are in "edit" mode, fetch the data.
        GetRiwayatPekerjaanData(ID);
      } else {
        // If we are in "add" mode, reset the form to its default state.
        reset();
      }
    }
  }, [ID, open]);

  useEffect(() => {
    if (noJumlahKaryawan) {
      setValue("jumlahKaryawan", "0");
    }
  }, [noJumlahKaryawan, setValue]);
  return (
    <Backdrop
      sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
      open={open}
    >
      <Box component={"form"} onSubmit={handleSubmit(onSubmit)} noValidate>
        <SquareGrid container>
          <ScrollGrid container>
            <Grid container justifyContent="flex-start" paddingLeft={"10.5%"} item xs={6}
              paddingBottom={
                {
                  xs: 5,
                  sm: 5,
                  md: 5,
                  lg: 5,
                  xl: 5
                }}>
              <Typography variant="subtitle1" color="black">
                {ID === -1
                  ? "Tambah Riwayat Pekerjaan"
                  : "Ubah Riwayat Pekerjaan"}
              </Typography>
            </Grid>
            <Grid container justifyContent="flex-end" item xs={6}>
              <Button onClick={handleModalClose}>Close</Button>
            </Grid>
            <InputTextFieldForm
              name="perusahaan"
              control={control}
              label="Perusahaan"
              error={errors.perusahaan}
            />
            <InputTextFieldForm
              name="jenisUsaha"
              control={control}
              label="Jenis Usaha"
              error={errors.jenisUsaha}
            />
            <InputTextFieldForm
              name="jabatanAwal"
              control={control}
              label="Jabatan Awal"
              error={errors.jabatanAwal}
            />
            <InputTextFieldForm
              name="jabatanAkhir"
              control={control}
              label="Jabatan Akhir"
              error={errors.jabatanAkhir}
            />
            <InputDateForm
              name="tahunMasuk"
              control={control}
              label="Tahun Masuk"
              disabled={false}
              error={errors.tahunMasuk}
            />
            {!masihBekerja && (
              <InputDateForm
                name="tahunKeluar"
                control={control}
                label="Tahun Keluar"
                disabled={masihBekerja}
                error={errors.tahunKeluar}
              />
            )}
            <Grid item xs={11}>
              <InputCheckboxForm
                name="masihBekerja"
                control={control}
                label="Masih Bekerja"
                checkboxLabel="Masih bekerja di perusahaan"
                targetFieldName="tahunKeluar"
                setValue={setValue}
                trigger={trigger}
              />
            </Grid>
            <InputTextFieldForm
              name="alasanBerhenti"
              control={control}
              label="Alasan Berhenti"
              error={errors.alasanBerhenti}

            />

            {!noJumlahKaryawan && (
              <InputTextFieldForm
                name="jumlahKaryawan"
                control={control}
                label="Jumlah Karyawan"
                inputMode="numeric"
                error={errors.jumlahKaryawan}
                disabled={noJumlahKaryawan}
              />
            )}
            <Grid item xs={11}>
              <InputCheckboxForm
                name="noJumlahKaryawan"
                control={control}
                label="Jumlah Karyawan Tidak Diketahui"
                checkboxLabel="Tidak tahu jumlah karyawan"
                targetFieldName="jumlahKaryawan"
                setValue={setValue}
                trigger={trigger}
              />
            </Grid>
            <InputTextFieldForm
              name="atasanLangsung"
              control={control}
              label="Atasan Langsung"
              error={errors.atasanLangsung}
            />
            <InputTextFieldForm
              name="direktur"
              control={control}
              label="Direktur"
              error={errors.direktur}
            />
            {!noGaji && (
              <InputTextFieldForm
                name="gajiTerakhir"
                control={control}
                label="Gaji Terakhir"
                inputMode="numeric"
                disabled={noGaji}
                error={errors.gajiTerakhir}
              />
            )}
            <Grid item xs={11}>
              <InputCheckboxForm
                name="noGaji"
                control={control}
                label="No Gaji"
                checkboxLabel="Memilih untuk tidak memberitahu gaji"
                targetFieldName="gajiTerakhir"
                setValue={setValue}
              />
            </Grid>
            <Grid
              container
              justifyContent="center"
              item
              lg={12}
              sx={{ mt: 3 }}
              paddingTop={{
                xs: 2,
                sm: 2,
                md: 2,
                lg: 2,
                xl: 2
              }}
              paddingBottom={{
                xs: 2,
                sm: 2,
                md: 2,
                lg: 2,
                xl: 2
              }}
            >
              <FullButton variant="contained" type="submit" disabled={isAdding || isEditing}>
                {(isAdding || isEditing) ? "Saving..." : "Save"}
              </FullButton>
            </Grid>
          </ScrollGrid>
        </SquareGrid>
      </Box>
    </Backdrop>
  );
}